/**
 * Server-fetched user role hook.
 *
 * Source of truth for auth-gate decisions in route group layouts. Replaces the
 * persisted-Zustand `userRole` read for security-sensitive paths — devtools-edits
 * of localStorage cannot bypass this gate, because the hook always queries
 * `profiles.role` via the Supabase client on every layout mount.
 *
 * Pairs with Phase 110 Plan 110-01, which made `profiles.role` the canonical
 * server-side role source (never the JWT-embedded user-metadata claim, which
 * any user can self-mutate via auth.updateUser). This hook consumes that same
 * canonical column from the client.
 *
 * IMPORTANT: this module does NOT import `useProfileStore` — keeping it
 * store-free is intentional, so it cannot be poisoned by tampered Zustand state.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * PERSONA — which product this account uses. Self-selected at onboarding.
 *
 * 'admin' is NOT a persona. Administrative authority lives in
 * `public.admin_roles` and is read via `useIsAdmin()`. See
 * .planning/ADMIN-AUTHORITY-MODEL.md and the ADMIN AUTHORITY INVARIANT in
 * CLAUDE.md.
 */
export type UserPersona = 'startup' | 'investor' | 'pending';

/**
 * The AUTHORIZATION role the route guards act on: a persona, or 'admin' when
 * the account holds `admin_roles` membership. Authority outranks persona.
 */
export type ServerUserRole = 'startup' | 'investor' | 'admin' | null;

/**
 * Minimal structural type for the Supabase client surface this module uses.
 * Declared locally so `fetchServerRole` can be unit-tested with a stub instead
 * of the real singleton.
 */
type RoleQueryClient = {
  from: (table: string) => {
    select: (columns: string) => {
      eq: (column: string, value: string) => {
        maybeSingle: () => Promise<{ data: unknown; error: unknown }>;
      };
    };
  };
};

/**
 * Does this account hold ADMINISTRATIVE AUTHORITY?
 *
 * Authority is membership in `public.admin_roles` and nothing else. RLS lets a
 * user read their OWN row ("Admins can read own role": `auth.uid() = user_id`),
 * so this is a direct, ordinary query — no RPC needs exposing. `is_admin(uuid)`
 * deliberately is NOT exposed to `authenticated`: it takes an arbitrary user id,
 * so an exposed version would let anyone enumerate who the administrators are.
 *
 * FAILS CLOSED. Any error yields `false`. "We could not find out" must never be
 * read as "probably an admin" — the blast radius of a false positive is the
 * entire admin surface, while a false negative is a retryable denial for the
 * handful of real administrators.
 *
 * Exported for tests; app code uses the `useIsAdmin` hook below.
 */
export async function fetchIsAdmin(
  client: RoleQueryClient,
  userId: string | undefined,
): Promise<boolean> {
  if (!userId) return false;
  try {
    const { data, error } = await client
      .from('admin_roles')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Resolve the authorization role the route-group guards act on.
 *
 * ONE MODEL (.planning/ADMIN-AUTHORITY-MODEL.md):
 *   `public.admin_roles` is the SOLE source of administrative authority;
 *   `profiles.role` is PERSONA ONLY and never decides admin.
 *
 * WHY THE PREVIOUS SHAPE WAS WRONG. It required `profiles.role='admin'` AND
 * membership — an AND of two stores, not one definition. `is_admin()` in
 * Postgres reads membership ALONE, so an administrator whose persona was
 * 'startup' was an admin to ~30 RLS policies and not an admin here; and a
 * persona-only admin was routed into the admin shell by index.tsx, refused
 * here, and bounced into an infinite redirect loop.
 *
 * Authority is therefore asked FIRST, exactly as `resolveAuthorizationRole`
 * does in `supabase/functions/_shared/auth.ts` — one rule, three layers, no
 * possibility of disagreement.
 *
 * A failed PROFILE read throws, so `resolveGuardState` renders its retry screen
 * rather than reading a network blip as "unauthorized". A failed MEMBERSHIP read
 * fails closed to "not an admin".
 *
 * Exported for tests; app code uses the `useUserRole` hook below.
 */
export async function fetchServerRole(
  client: RoleQueryClient,
  userId: string | undefined,
): Promise<ServerUserRole> {
  if (!userId) return null;

  // BOTH READS IN FLIGHT AT ONCE. Authority still WINS, but it must not be
  // WAITED FOR first: awaiting admin_roles and only then querying profiles made
  // every route-guard mount pay two sequential round-trips (~128ms each against
  // production) where it previously paid one. The guards block rendering until
  // this resolves — `if (guardState !== 'authorized') return <View/>` — so that
  // doubling is felt directly as the UI not reacting.
  //
  // Ordinary users now pay one round-trip of wall-clock for two queries, which
  // is the same latency as before this file started asking about authority.
  const [isAdmin, profileRes] = await Promise.all([
    fetchIsAdmin(client, userId),
    client.from('profiles').select('role').eq('id', userId).maybeSingle(),
  ]);

  if (isAdmin) return 'admin';

  const { data, error } = profileRes;
  if (error) throw error;

  const persona = (data as { role?: string } | null)?.role ?? null;
  // 'admin' is a retired persona value (migration 20260807120000 backfilled it
  // away and the trigger now rejects it from every writer). Reaching here with
  // it means no authority was found, so it must not be honoured as a role.
  if (persona === 'admin' || persona === 'pending') return null;
  return (persona as ServerUserRole) ?? null;
}

/**
 * Fetch the authenticated user's role from the server-controlled `profiles.role`
 * column. Returns `null` when no profile row exists for the given userId
 * (treat as unauthorized at the call site). Throws on Postgres error so React
 * Query surfaces the failure to the caller.
 *
 * Cached for 30 seconds via React Query — refetches on every layout mount in
 * practice (mounts are infrequent), but caches across rapid re-renders within
 * the same screen.
 */
export function useUserRole(userId: string | undefined) {
  return useQuery({
    queryKey: ['user-role', userId],
    enabled: !!userId,
    // Treat as fresh for 30s — refetches on every mount but caches across rapid re-renders.
    staleTime: 30_000,
    queryFn: (): Promise<ServerUserRole> =>
      fetchServerRole(supabase as unknown as RoleQueryClient, userId),
  });
}

/**
 * Administrative authority for the current user, server-resolved.
 *
 * THE single client-side answer to "is this an administrator". Every gate —
 * routing, the /(admin) guard, admin-only UI — reads this, so the client cannot
 * develop a second opinion the way it did when `profiles.role === 'admin'` was
 * scattered across 19 files.
 *
 * Shares a React Query key with nothing else and is cached for 30s, matching
 * `useUserRole`. Authority is re-read on every layout mount, so a revoked admin
 * loses the shell within one navigation rather than on next app launch.
 */
export function useIsAdmin(userId: string | undefined) {
  const query = useQuery({
    queryKey: ['is-admin', userId],
    enabled: !!userId,
    staleTime: 30_000,
    queryFn: (): Promise<boolean> =>
      fetchIsAdmin(supabase as unknown as RoleQueryClient, userId),
  });

  return {
    /** True ONLY when membership was positively confirmed. */
    isAdmin: query.data === true,
    /** Callers that route must wait for this — see index.tsx. */
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
